import { createHash, randomBytes } from "node:crypto";
import { mkdir, readFile, rename, unlink, writeFile } from "node:fs/promises";
import { homedir } from "node:os";
import { dirname, join } from "node:path";
import type pino from "pino";
import * as YAML from "yaml";

// ────────────────────────────────────────────────────────────────────────────
// GitLab OAuth (PKCE) — gitlab.com only, single-account-per-host.
//
// Flow:
//   1. App calls `initiate(provider)` → daemon mints PKCE pair + state, parks
//      the verifier in an in-memory map keyed by state, returns the GitLab
//      authorize URL.
//   2. User consents on gitlab.com, GitLab redirects to oauth.appostle.app/
//      gitlab/callback?code=…&state=…. The callback Worker postMessages the
//      pair back to the app, which forwards them via `complete(provider,
//      code, state)`.
//   3. Daemon looks up the verifier by state, POSTs to /oauth/token with
//      `grant_type=authorization_code` (no client_secret — pure PKCE).
//   4. Token persists to `$APPOSTLE_HOME/credentials.json` (mode 0600) AND
//      `~/.config/glab/config.yml` so the existing `glab` shell-outs see it.
//   5. On every subsequent GitLab API call the daemon checks `expiresAt` and
//      refreshes inline if within 60s. Lazy refresh keeps the architecture
//      simple — no background timers, no race conditions on app sleep/wake.
//
// PKCE means the authorization `code` alone is useless to an attacker —
// they'd also need the `code_verifier` which never leaves daemon memory.
// State is single-use, expires in 10 min, prevents CSRF.
// ────────────────────────────────────────────────────────────────────────────

export type OAuthProvider = "gitlab";

const GITLAB_CLIENT_ID = "7783dfb277ccf3dbce35a9d54c3c0aec24ed2006cb2d171e0749fd8444ed4f7c";
const GITLAB_AUTHORIZE_URL = "https://gitlab.com/oauth/authorize";
const GITLAB_TOKEN_URL = "https://gitlab.com/oauth/token";
const GITLAB_REVOKE_URL = "https://gitlab.com/oauth/revoke";
const GITLAB_USER_URL = "https://gitlab.com/api/v4/user";
const GITLAB_REDIRECT_URI = "https://oauth.appostle.app/gitlab/callback";
// `api` is the only scope that grants both read + write on issues/MRs.
// GitLab has no granular `write_issues` scope; `api` is the smallest grant
// that supports the "New issue" feature.
const GITLAB_SCOPES = "api";

const STATE_TTL_MS = 10 * 60 * 1_000;
const REFRESH_LEEWAY_MS = 60 * 1_000;

interface PendingAuthorization {
  provider: OAuthProvider;
  codeVerifier: string;
  expiresAt: number;
}

export interface StoredCredential {
  host: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  username: string | null;
}

export interface CredentialsFile {
  gitlab?: StoredCredential;
}

export interface OAuthInitiateResult {
  authorizeUrl: string;
}

export interface OAuthCompleteResult {
  username: string | null;
}

export interface OAuthStatus {
  signedIn: boolean;
  username: string | null;
  expiresAt: number | null;
}

export class OAuthError extends Error {
  readonly kind: "missing_state" | "exchange_failed" | "refresh_failed" | "network" | "config";

  constructor(
    kind: OAuthError["kind"],
    message: string,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.kind = kind;
    this.name = "OAuthError";
  }
}

export interface OAuthService {
  initiate(provider: OAuthProvider): Promise<OAuthInitiateResult>;
  complete(provider: OAuthProvider, code: string, state: string): Promise<OAuthCompleteResult>;
  status(provider: OAuthProvider): Promise<OAuthStatus>;
  signOut(provider: OAuthProvider): Promise<void>;
  /**
   * Refresh the stored token if it's expired or within the leeway window.
   * No-op when no credential exists or when the token is still fresh.
   * Called inline before every gitlab-service shell-out (lazy refresh).
   */
  refreshIfStale(provider: OAuthProvider): Promise<void>;
}

interface CreateOAuthServiceOptions {
  appostleHome: string;
  logger: pino.Logger;
  /** Override for tests — defaults to global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Override for tests — defaults to glab's platform-specific config path. */
  glabConfigPath?: string;
  /** Override for tests — defaults to `Date.now`. */
  now?: () => number;
}

export function createOAuthService(options: CreateOAuthServiceOptions): OAuthService {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch;
  const now = options.now ?? Date.now;
  const credentialsPath = join(options.appostleHome, "credentials.json");
  const glabConfigPath = options.glabConfigPath ?? defaultGlabConfigPath();
  const oauthLogger = options.logger.child({ module: "oauth-service" });

  const pending = new Map<string, PendingAuthorization>();

  function gcExpiredStates(): void {
    const cutoff = now();
    for (const [state, entry] of pending.entries()) {
      if (entry.expiresAt <= cutoff) {
        pending.delete(state);
      }
    }
  }

  return {
    async initiate(provider) {
      if (provider !== "gitlab") {
        throw new OAuthError("config", `Unsupported OAuth provider: ${provider}`);
      }
      gcExpiredStates();
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = computeCodeChallenge(codeVerifier);
      const state = generateState();
      pending.set(state, {
        provider,
        codeVerifier,
        expiresAt: now() + STATE_TTL_MS,
      });
      const authorizeUrl = buildAuthorizeUrl({ codeChallenge, state });
      oauthLogger.info({ provider, state: maskState(state) }, "oauth.initiate");
      return { authorizeUrl };
    },

    async complete(provider, code, state) {
      if (provider !== "gitlab") {
        throw new OAuthError("config", `Unsupported OAuth provider: ${provider}`);
      }
      gcExpiredStates();
      const entry = pending.get(state);
      if (!entry || entry.provider !== provider) {
        throw new OAuthError(
          "missing_state",
          "Sign-in expired or didn't originate from this device. Try again.",
        );
      }
      // Single-use: drop the verifier the moment we use it, even if the
      // exchange fails. Replay is impossible.
      pending.delete(state);
      const tokens = await exchangeCodeForToken({
        fetchImpl,
        code,
        codeVerifier: entry.codeVerifier,
      });
      const username = await fetchGitLabUsername(fetchImpl, tokens.access_token);
      const credential: StoredCredential = {
        host: "gitlab.com",
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresAt: now() + tokens.expires_in * 1_000,
        username,
      };
      await persistCredential(credentialsPath, credential, oauthLogger);
      await writeGlabConfig(glabConfigPath, credential, oauthLogger);
      oauthLogger.info({ provider, username }, "oauth.complete.success");
      return { username };
    },

    async status(provider) {
      if (provider !== "gitlab") {
        return { signedIn: false, username: null, expiresAt: null };
      }
      const credential = await readCredential(credentialsPath, oauthLogger);
      if (!credential) {
        return { signedIn: false, username: null, expiresAt: null };
      }
      return {
        signedIn: true,
        username: credential.username,
        expiresAt: credential.expiresAt,
      };
    },

    async signOut(provider) {
      if (provider !== "gitlab") return;
      const credential = await readCredential(credentialsPath, oauthLogger);
      if (credential) {
        // Best-effort revoke — swallow errors so a network failure doesn't
        // strand the user with a "signed in" UI when their local state is
        // gone. The token will still expire on its own.
        try {
          await revokeToken(fetchImpl, credential.accessToken);
        } catch (error) {
          oauthLogger.warn({ err: error }, "oauth.signout.revoke_failed");
        }
      }
      await deleteCredential(credentialsPath, oauthLogger);
      await removeGlabHost(glabConfigPath, oauthLogger);
      oauthLogger.info({ provider }, "oauth.signout.complete");
    },

    async refreshIfStale(provider) {
      if (provider !== "gitlab") return;
      const credential = await readCredential(credentialsPath, oauthLogger);
      if (!credential) return;
      if (credential.expiresAt > now() + REFRESH_LEEWAY_MS) return;
      try {
        const tokens = await refreshAccessToken({
          fetchImpl,
          refreshToken: credential.refreshToken,
        });
        const next: StoredCredential = {
          ...credential,
          // GitLab rotates the refresh token on every refresh — must persist
          // the new one or the next refresh will be rejected.
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresAt: now() + tokens.expires_in * 1_000,
        };
        await persistCredential(credentialsPath, next, oauthLogger);
        await writeGlabConfig(glabConfigPath, next, oauthLogger);
        oauthLogger.info({ provider }, "oauth.refresh.success");
      } catch (error) {
        // Refresh failed (user revoked from gitlab.com, network down, etc.).
        // Clear local state so the UI swings back to the sign-in card on the
        // next status check. Throwing would make every gitlab-service call
        // fail loudly; we'd rather degrade to "not signed in".
        oauthLogger.warn({ err: error }, "oauth.refresh.failed_clearing");
        await deleteCredential(credentialsPath, oauthLogger);
        await removeGlabHost(glabConfigPath, oauthLogger);
      }
    },
  };
}

// ─── PKCE primitives ──────────────────────────────────────────────────────

function generateCodeVerifier(): string {
  // RFC 7636 §4.1: 43–128 chars from [A-Z][a-z][0-9]-._~
  // 32 random bytes → 43 chars after base64url (no padding).
  return base64url(randomBytes(32));
}

function computeCodeChallenge(verifier: string): string {
  return base64url(createHash("sha256").update(verifier).digest());
}

function generateState(): string {
  return base64url(randomBytes(24));
}

function base64url(buf: Buffer): string {
  return buf.toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function maskState(state: string): string {
  if (state.length <= 8) return "***";
  return `${state.slice(0, 4)}…${state.slice(-4)}`;
}

// ─── URL building ─────────────────────────────────────────────────────────

function buildAuthorizeUrl(params: { codeChallenge: string; state: string }): string {
  const url = new URL(GITLAB_AUTHORIZE_URL);
  url.searchParams.set("client_id", GITLAB_CLIENT_ID);
  url.searchParams.set("redirect_uri", GITLAB_REDIRECT_URI);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("state", params.state);
  url.searchParams.set("scope", GITLAB_SCOPES);
  url.searchParams.set("code_challenge", params.codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

// ─── Token exchange + refresh ─────────────────────────────────────────────

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope?: string;
  created_at?: number;
}

async function exchangeCodeForToken(params: {
  fetchImpl: typeof fetch;
  code: string;
  codeVerifier: string;
}): Promise<TokenResponse> {
  const body = new URLSearchParams({
    client_id: GITLAB_CLIENT_ID,
    grant_type: "authorization_code",
    code: params.code,
    redirect_uri: GITLAB_REDIRECT_URI,
    code_verifier: params.codeVerifier,
  });
  return postTokenEndpoint(params.fetchImpl, body, "exchange_failed");
}

async function refreshAccessToken(params: {
  fetchImpl: typeof fetch;
  refreshToken: string;
}): Promise<TokenResponse> {
  const body = new URLSearchParams({
    client_id: GITLAB_CLIENT_ID,
    grant_type: "refresh_token",
    refresh_token: params.refreshToken,
    redirect_uri: GITLAB_REDIRECT_URI,
  });
  return postTokenEndpoint(params.fetchImpl, body, "refresh_failed");
}

async function postTokenEndpoint(
  fetchImpl: typeof fetch,
  body: URLSearchParams,
  errorKind: "exchange_failed" | "refresh_failed",
): Promise<TokenResponse> {
  let response: Response;
  try {
    response = await fetchImpl(GITLAB_TOKEN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
      body: body.toString(),
    });
  } catch (error) {
    throw new OAuthError("network", "Network error while talking to GitLab.", error);
  }
  const text = await response.text();
  if (!response.ok) {
    throw new OAuthError(
      errorKind,
      `GitLab token endpoint returned ${response.status}: ${truncate(text, 200)}`,
    );
  }
  let parsed: TokenResponse;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new OAuthError(errorKind, "GitLab returned a non-JSON token response.");
  }
  if (
    typeof parsed.access_token !== "string" ||
    typeof parsed.refresh_token !== "string" ||
    typeof parsed.expires_in !== "number"
  ) {
    throw new OAuthError(errorKind, "GitLab token response is missing required fields.");
  }
  return parsed;
}

async function revokeToken(fetchImpl: typeof fetch, token: string): Promise<void> {
  const body = new URLSearchParams({
    client_id: GITLAB_CLIENT_ID,
    token,
  });
  const response = await fetchImpl(GITLAB_REVOKE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!response.ok && response.status !== 401) {
    // 401 means the token was already invalid — fine. Other failures we log
    // and move on (signOut is best-effort by design).
    throw new OAuthError("network", `GitLab revoke endpoint returned ${response.status}`);
  }
}

async function fetchGitLabUsername(
  fetchImpl: typeof fetch,
  accessToken: string,
): Promise<string | null> {
  try {
    const response = await fetchImpl(GITLAB_USER_URL, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
    });
    if (!response.ok) return null;
    const json = (await response.json()) as { username?: unknown };
    return typeof json.username === "string" ? json.username : null;
  } catch {
    return null;
  }
}

// ─── Credentials file persistence ─────────────────────────────────────────

async function readCredential(path: string, log: pino.Logger): Promise<StoredCredential | null> {
  try {
    const raw = await readFile(path, "utf8");
    const parsed = JSON.parse(raw) as CredentialsFile;
    return parsed.gitlab ?? null;
  } catch (error) {
    if (isNotFound(error)) return null;
    log.warn({ err: error, path }, "oauth.credentials.read_failed");
    return null;
  }
}

async function persistCredential(
  path: string,
  credential: StoredCredential,
  log: pino.Logger,
): Promise<void> {
  await mkdir(dirname(path), { recursive: true });
  let current: CredentialsFile = {};
  try {
    const raw = await readFile(path, "utf8");
    current = JSON.parse(raw) as CredentialsFile;
  } catch (error) {
    if (!isNotFound(error)) {
      log.warn({ err: error, path }, "oauth.credentials.read_failed_overwriting");
    }
  }
  const next: CredentialsFile = { ...current, gitlab: credential };
  // Atomic write: tmp file → rename. Mode 0600 (owner-only).
  const tmpPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  await writeFile(tmpPath, JSON.stringify(next, null, 2), { mode: 0o600 });
  await rename(tmpPath, path);
}

async function deleteCredential(path: string, log: pino.Logger): Promise<void> {
  let current: CredentialsFile = {};
  try {
    const raw = await readFile(path, "utf8");
    current = JSON.parse(raw) as CredentialsFile;
  } catch (error) {
    if (isNotFound(error)) return;
    log.warn({ err: error, path }, "oauth.credentials.delete_read_failed");
    return;
  }
  delete current.gitlab;
  if (Object.keys(current).length === 0) {
    try {
      await unlink(path);
    } catch (error) {
      if (!isNotFound(error)) {
        log.warn({ err: error, path }, "oauth.credentials.unlink_failed");
      }
    }
    return;
  }
  const tmpPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  await writeFile(tmpPath, JSON.stringify(current, null, 2), { mode: 0o600 });
  await rename(tmpPath, path);
}

// ─── glab config sync ─────────────────────────────────────────────────────

/**
 * Resolves the platform-specific glab config file path. glab uses Go's
 * `os.UserConfigDir()` semantics:
 *   - macOS:  ~/Library/Application Support/glab-cli/config.yml
 *   - Linux:  $XDG_CONFIG_HOME/glab-cli/config.yml  (~/.config/glab-cli/...)
 *   - Windows: %AppData%\glab-cli\config.yml
 * Note the `glab-cli` directory name (NOT `glab`) — easy to get wrong.
 */
function defaultGlabConfigPath(): string {
  const home = homedir();
  if (process.platform === "darwin") {
    return join(home, "Library", "Application Support", "glab-cli", "config.yml");
  }
  if (process.platform === "win32") {
    const appData = process.env.APPDATA;
    if (appData) return join(appData, "glab-cli", "config.yml");
    return join(home, "AppData", "Roaming", "glab-cli", "config.yml");
  }
  // Linux + others: respect $XDG_CONFIG_HOME, fall back to ~/.config.
  const xdg = process.env.XDG_CONFIG_HOME;
  return join(xdg && xdg.length > 0 ? xdg : join(home, ".config"), "glab-cli", "config.yml");
}

async function writeGlabConfig(
  path: string,
  credential: StoredCredential,
  log: pino.Logger,
): Promise<void> {
  await mkdir(dirname(path), { recursive: true });
  // Read-modify-write. `yaml` lib preserves comments + key order so we don't
  // wipe a user's manual customizations when we add our token.
  let doc: YAML.Document.Parsed;
  try {
    const raw = await readFile(path, "utf8");
    doc = YAML.parseDocument(raw) as YAML.Document.Parsed;
    if (doc.errors.length > 0) {
      log.warn(
        { errors: doc.errors.map((e) => e.message), path },
        "oauth.glab.parse_errors_replacing",
      );
      doc = YAML.parseDocument("{}") as YAML.Document.Parsed;
    }
  } catch (error) {
    if (isNotFound(error)) {
      doc = YAML.parseDocument("{}") as YAML.Document.Parsed;
    } else {
      log.warn({ err: error, path }, "oauth.glab.read_failed_replacing");
      doc = YAML.parseDocument("{}") as YAML.Document.Parsed;
    }
  }

  const hosts = ensureMap(doc, "hosts");
  const hostEntry = ensureMapAt(hosts, credential.host);
  hostEntry.set("token", credential.accessToken);
  // CRITICAL: tells glab to send `Authorization: Bearer …` instead of the
  // `PRIVATE-TOKEN: …` header it uses for Personal Access Tokens. OAuth
  // tokens are rejected with a generic "401 Unauthorized" by GitLab if sent
  // the PAT way. NOTE: the field is `is_oauth2` (with the digit), NOT
  // `is_oauth` — found by `strings`-ing the glab binary; not in the docs.
  hostEntry.set("is_oauth2", true);
  hostEntry.set("api_protocol", "https");
  hostEntry.set("git_protocol", "https");
  if (credential.username) {
    hostEntry.set("user", credential.username);
  }

  const tmpPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  await writeFile(tmpPath, doc.toString(), { mode: 0o600 });
  await rename(tmpPath, path);
}

async function removeGlabHost(path: string, log: pino.Logger): Promise<void> {
  let doc: YAML.Document.Parsed;
  try {
    const raw = await readFile(path, "utf8");
    doc = YAML.parseDocument(raw) as YAML.Document.Parsed;
  } catch (error) {
    if (isNotFound(error)) return;
    log.warn({ err: error, path }, "oauth.glab.remove_read_failed");
    return;
  }
  const hosts = doc.get("hosts");
  if (!YAML.isMap(hosts)) return;
  hosts.delete("gitlab.com");
  // If hosts is now empty, drop the key entirely so we don't leave `hosts: {}`
  // littering the file.
  if (hosts.items.length === 0) {
    doc.delete("hosts");
  }
  const tmpPath = `${path}.tmp-${process.pid}-${Date.now()}`;
  await writeFile(tmpPath, doc.toString(), { mode: 0o600 });
  await rename(tmpPath, path);
}

function ensureMap(doc: YAML.Document.Parsed, key: string): YAML.YAMLMap {
  const existing = doc.get(key);
  if (YAML.isMap(existing)) return existing;
  const created = new YAML.YAMLMap();
  doc.set(key, created);
  return created;
}

function ensureMapAt(parent: YAML.YAMLMap | YAML.Document.Parsed, key: string): YAML.YAMLMap {
  const existing = parent.get(key);
  if (YAML.isMap(existing)) return existing;
  const created = new YAML.YAMLMap();
  parent.set(key, created);
  return created;
}

// ─── Misc helpers ─────────────────────────────────────────────────────────

function isNotFound(error: unknown): boolean {
  return Boolean(
    error &&
      typeof error === "object" &&
      "code" in error &&
      (error as { code?: unknown }).code === "ENOENT",
  );
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

// Exported only for tests.
export const __test_helpers = {
  generateCodeVerifier,
  computeCodeChallenge,
  buildAuthorizeUrl,
};
